import {
  Fn,
  If,
  type ShaderNodeObject,
  clamp,
  dot,
  float,
  length,
  max,
  mix,
  normalize,
  pow,
  reflect,
  smoothstep,
  vec3,
} from 'three/tsl';
import type { Node } from 'three/webgpu';

//light configs
const ambientLight = Fn(
  ([lightColor, lightIntensity]: [
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>
  ]) => {
    return lightColor.mul(lightIntensity);
  }
);

const directionalLight = Fn(
  ([
    lightColor,
    lightIntensity,
    normal,
    lightPosition,
    viewDirection,
    specularPower,
  ]: [
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>
  ]) => {
    const lightDirection = normalize(lightPosition);
    const lightRefrection = reflect(lightDirection.negate(), normal);

    const specular = dot(lightRefrection, viewDirection).negate();
    specular.assign(max(0.0, specular));
    specular.assign(pow(specular, specularPower));
    const shading = dot(normal, lightDirection);
    shading.assign(max(0.0, shading));

    return lightColor.mul(lightIntensity).mul(shading.add(specular));
  }
);

const pointLight = Fn(
  ([
    lightColor,
    lightIntensity,
    normal,
    lightPosition,
    viewDirection,
    specularPower,
    position,
    lightDecay,
  ]: [
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>
  ]) => {
    const lightDelta = lightPosition.sub(position);

    const lightDistance = length(lightDelta);

    const lightDirection = normalize(lightDelta);

    const lightRefrection = reflect(lightDirection.negate(), normal);

    const specular = dot(lightRefrection, viewDirection).negate();
    specular.assign(max(0.0, specular));
    specular.assign(pow(specular, specularPower));
    const shading = dot(normal, lightDirection);
    shading.assign(max(0.0, shading));

    //light decay
    const decay = float(lightDistance.mul(lightDecay).oneMinus());
    decay.assign(max(0.0, decay));
    return lightColor.mul(lightIntensity).mul(decay).mul(shading.add(specular));
    //return shading;
    //return vec3(decay);
  }
);

const threeColorGradient = Fn(
  ([direction, colorA, colorAa, colorB, colorC]: [
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>,
    ShaderNodeObject<Node>
  ]) => {
    direction.assign(clamp(direction, 0.0, 1.0));

    const color = vec3(0.0);

    const step1 = 0.045;
    const step2 = 0.5;

    If(direction.lessThan(step1), () => {
      const v = smoothstep(0.0, step1, direction);

      color.assign(mix(colorA, colorAa, v));
    })
      .ElseIf(direction.lessThan(step2), () => {
        const v = smoothstep(step1, step2, direction);

        color.assign(mix(colorAa, colorB, v));
      })
      .Else(() => {
        const v = smoothstep(step2, 1.0, direction);

        color.assign(mix(colorB, colorC, v));
      });

    return color;
  }
);

// const mixColor = Fn((([vLow, vHigh, direction])=> {

//   const color = vec3(0.0)

//   const v = vec3(0)

//   If(direction.lessThan(vHigh), ()=> {
//     v.assign(smoothstep(vLow, vHigh, direction))
//   })

//   return v
// }))

export { directionalLight, ambientLight, pointLight, threeColorGradient };

//example on how to use light
//  ambient light

//   light.addAssign(
//     ambientLight(
//       vec3(1, 0, 0), //light color
//       float(0.03) //light intensity
//     )
//   );

//   //  directional light
//   light.addAssign(
//     directionalLight(
//       vec3(uColorA), //light color
//       float(1.0), //light intensity
//       normal, //vNormal
//       uniform(
//         //lightPosition
//         vec3(
//           controls.lightPos[0],
//           controls.lightPos[1],
//           controls.lightPos[2]
//         ),
//         'vec3'
//       ),
//       viewDirection, // camera view direction
//       float(20.0) //specular power
//     )
//   );

//   light.addAssign(
//     pointLight(
//       vec3(1.0, 0.1, 0.1), //light color
//       float(1.0), //light intensity
//       normal, //vNormal
//       uniform(
//         //lightPosition
//         vec3(controls.pLightX, controls.pLightY, controls.pLightZ),
//         'vec3'
//       ),
//       viewDirection, // camera view direction
//       float(20.0), //specular power
//       vPosition, //model position
//       uniform(controls.decay, 'float')
//     )
//   );

// const pointLightShading = pointLight(
//   vec3(1.0), //light color
//   float(1.0), //light intensity
//   normal, //vNormal
//   pointLightDirection,
//   viewDirection, // camera view direction
//   float(20.0), //specular power
//   vPosition, //model position
//   uniform(controls.decay, 'float')
// );
